import classNames from "classnames";
import { FC, MouseEvent, TouchEvent, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ReactComponent as ArrowIcon } from "assets/images/common/arrow.svg";

import { ImageType } from "entities/common";

import styles from "./style.module.scss";

type PropTypes = {
  images: ImageType[];
  startIndex: number;
  onClose: () => void;
};

const PhotoViewer: FC<PropTypes> = ({ images, startIndex, onClose }) => {
  const [index, setIndex] = useState(startIndex);
  const touchX = useRef<number | null>(null);

  const prev = useCallback(
    () => setIndex((current) => (current - 1 + images.length) % images.length),
    [images.length],
  );
  const next = useCallback(
    () => setIndex((current) => (current + 1) % images.length),
    [images.length],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") prev();
      if (event.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose, prev, next]);

  const stop = (event: MouseEvent) => event.stopPropagation();

  const onTouchStart = (event: TouchEvent) => {
    touchX.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: TouchEvent) => {
    if (touchX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchX.current;
    if (delta > 40) prev();
    else if (delta < -40) next();
    touchX.current = null;
  };

  const multiple = images.length > 1;

  return createPortal(
    <div className={styles.viewer} onClick={onClose}>
      <button type="button" className={styles.viewerClose} onClick={onClose} aria-label="Close">
        ×
      </button>

      {multiple && (
        <button
          type="button"
          className={classNames(styles.viewerNav, styles.viewerPrev)}
          onClick={(event) => {
            stop(event);
            prev();
          }}
          aria-label="Previous"
        >
          <ArrowIcon />
        </button>
      )}

      <img
        className={styles.viewerImage}
        src={images[index].url}
        alt={images[index].original_name}
        onClick={stop}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      />

      {multiple && (
        <button
          type="button"
          className={classNames(styles.viewerNav, styles.viewerNext)}
          onClick={(event) => {
            stop(event);
            next();
          }}
          aria-label="Next"
        >
          <ArrowIcon />
        </button>
      )}

      {multiple && (
        <span className={styles.viewerCounter} onClick={stop}>
          {index + 1} / {images.length}
        </span>
      )}
    </div>,
    document.body,
  );
};

export default PhotoViewer;
